(ns app.front.view.admin.domain-groups.municipality-categories
  "Frontend controls for the CloudPermit admin to select which categories are available for a municipality"
  (:require [app.domain.group.permit-type-config.model :as ptc-model]
            [app.front.util :as futil]
            [app.front.view.admin.subs :as admin-subs]
            [clojure.set :as set]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))


;; All existing categories grouped by permit type
(rf/reg-sub ::permit-type->categories
  (fn [db [_ tier]]
    (cond-> (:app.front.view.admin.domain-groups.municipality-groups/categories db)
      (= tier :upper-tier) (select-keys [:permit-type/P]))))


(rf/reg-sub ::current-list
  :-> ::current-list)


(rf/reg-event-db ::toggle-current-list
  (fn [db [_ value]]
    (update db ::current-list #(when (not= % value) value))))


(defn filter-categories-param
  "Removes non-applicable or empty category selections"
  [municipality-params]
  (let [selected-permit-type? (set (:permit-types municipality-params))]
    (update municipality-params
            :categories
            (fn [permit-type->categories]
              (or (->> permit-type->categories
                       (p/remove-vals empty?)
                       (p/filter-keys selected-permit-type?))
                  {})))))

(defn category-list
  "A list for the admin to select which categories are available for the municipality"
  [{:keys [tr edit-fn permit-type permit-types categories available-categories disabled? open?]}]
  (when open?
    [:div.p-4
     {:on-click #(.stopPropagation %)}
     [:legend.bx--label
      (tr [:admin/municipality-categories])]
     (futil/for-frag [category available-categories]
       (cc/checkbox
         {:key        (str permit-type category)
          :id         (str permit-type category)
          :on-change  (fn [checked?]
                        (edit-fn :categories
                                 (update categories
                                         permit-type
                                         #(if checked?
                                            (conj (set %) category)
                                            (disj (set %) category)))))
          :label-text (tr [category])
          :disabled   (or disabled?
                          (not (contains? (set permit-types) permit-type)))
          :checked    (-> (get categories permit-type)
                          set
                          (contains? category))}))]))


(defn with-default-categories [categories permit-type available-categories]
  (if (empty? (get categories permit-type))
    (assoc categories
           permit-type
           (ptc-model/filter-default-category-seq permit-type available-categories))
    categories))


(defn- category-list-header
  "Header for the category list of a single permit type;
  checkbox enabled/disables the group for that permit type,
  clicking on the header opens/closes the category list"
  [{:keys [tr edit-fn permit-types removed-permit-types pending-config-permit-types permit-type disabled?
           available-categories categories]}]
  [:div.flex.justify-between.items-center
   (when (seq available-categories)
     {:on-click #(>evt [::toggle-current-list permit-type])})
   [:div.flex.items-center.gap-2
    {:on-click #(.stopPropagation %)}
    (cc/checkbox
      {:key        (str "permit-type-" (name permit-type))
       :id         (str "permit-type-" (name permit-type))
       :on-change  (fn [checked?]
                     (edit-fn :permit-types ((if checked? conj disj) permit-types permit-type))
                     (edit-fn :removed-permit-types ((if checked? disj conj) (set removed-permit-types) permit-type))
                     (when checked?
                       (let [updated-categories (with-default-categories categories permit-type available-categories)]
                         (when (not= categories updated-categories)
                           (edit-fn :categories updated-categories)))))
       :label-text (tr [permit-type])
       :disabled   disabled?
       :checked    (-> permit-types set (contains? permit-type))})
    (when (contains? (set pending-config-permit-types) permit-type)
      [:span.inline-flex.items-center.rounded-full.bg-yellow-200.text-gray-700.font-bold.text-xs.px-2.py-1
       (tr [:admin/config-import-pending])])]
   (when (seq available-categories)
     (cc/icon
       {:icon        (if (= permit-type (<sub [::current-list]))
                       i/chevron-up
                       i/chevron-down)
        :description (tr [:review.config/selected-review-config])}))])


(defn permit-type-list
  "A list of each permit type and its categories the admin can enable/disable for a municipality"
  [{:keys [tr editing? tier saved-categories] :as opts}]
  (let [permit-type->categories (<sub [::permit-type->categories tier])]
    [:<>
     [:legend.bx--label
      (tr [:admin/permit-types])]
     (futil/for-frag [[permit-type] (->> permit-type->categories
                                         (sort-by (comp tr vector first)))
                      :let [avail-cats (some->> (set/union (-> permit-type->categories permit-type set)
                                                           (-> saved-categories permit-type set))
                                                (sort-by (comp tr vector))
                                                seq)
                            opts       (assoc opts
                                              :open? (= permit-type (<sub [::current-list]))
                                              :permit-type permit-type
                                              :available-categories avail-cats
                                              :edit-fn (fn [k v]
                                                         ;; TODO fix keyword; not always `new` municipality
                                                         (>evt [::admin-subs/dialog-edit :new-municipality k v])))]]
       [:div.p-3.cursor-pointer.border.border-grey.border-solid.bg-white
        [category-list-header opts]
        [category-list opts]])
     (when editing?
       [:p.mt-3 (str "Note that disabling a permit type will only make it inactive for new applications. "
                     "The corresponding application authority group will still remain in the system.")])]))
